import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import PadGrid from '../components/MusicPlayer/PadGrid'
import {
  emptyPads,
  PAD_COUNT,
  padLabel,
  type PadState,
} from '../components/MusicPlayer/padState'
import '../components/MusicPlayer/Pad.css'
import {
  disposeWorker,
  encodeToMp3,
  sniffSourceFormat,
} from '../audio/audioWorkerClient'
import { computeTargetKbpsForSize } from '../audio/computeTargetKbps'
import { TARGET_ENCODED_SIZE_BYTES } from '../audio/types'
import Dropzone from '../components/Dropzone/Dropzone'
import { type UploadFile } from '../upload/uploadAudio'
import { MP3_PLAYER_FIRMWARE } from '../upload/fetchFirmware'
import { extractZipAudio, isZipFile } from '../upload/unzip'
import { getEmptyMp3Blob } from '../upload/emptyMp3'
import { useToyMaker } from '../upload/useToyMaker'
import './MakeMusicPlayer.css'

const INTAKE_CONCURRENCY = 3

type Action =
  | { type: 'start'; index: number; sourceName: string }
  | { type: 'progress'; index: number; percent: number }
  | { type: 'done'; index: number; blob: Blob; objectUrl: string; skippedEncode: boolean; sourceName: string }
  | { type: 'error'; index: number; message: string; sourceName: string }
  | { type: 'clear'; index: number }
  | { type: 'reset' }

function reducer(state: PadState[], action: Action): PadState[] {
  switch (action.type) {
    case 'start':
      return state.map((s, i) =>
        i === action.index
          ? { status: 'encoding', sourceName: action.sourceName, progress: 0 }
          : s,
      )
    case 'progress':
      return state.map((s, i) =>
        i === action.index && s.status === 'encoding'
          ? { ...s, progress: action.percent }
          : s,
      )
    case 'done':
      return state.map((s, i) =>
        i === action.index
          ? {
              status: 'ready',
              sourceName: action.sourceName,
              encodedBlob: action.blob,
              objectUrl: action.objectUrl,
              skippedEncode: action.skippedEncode,
            }
          : s,
      )
    case 'error':
      return state.map((s, i) =>
        i === action.index
          ? { status: 'error', sourceName: action.sourceName, message: action.message }
          : s,
      )
    case 'clear':
      return state.map((s, i) => (i === action.index ? { status: 'empty' } : s))
    case 'reset':
      return emptyPads()
  }
}

function isAudioFile(file: File): boolean {
  if (file.type.startsWith('audio/')) return true
  const ext = file.name.toLowerCase().split('.').pop()
  return ext === 'mp3' || ext === 'wav' || ext === 'aac' || ext === 'm4a'
}

export default function MakeMusicPlayer() {
  const [pads, dispatch] = useReducer(reducer, undefined, emptyPads)
  const [playingIndex, setPlayingIndex] = useState<number | null>(null)
  const audioRef = useRef<HTMLAudioElement>(null)
  const padsRef = useRef<PadState[]>(pads)
  const playingIndexRef = useRef<number | null>(null)

  useEffect(() => {
    padsRef.current = pads
  })
  useEffect(() => {
    playingIndexRef.current = playingIndex
  })

  useEffect(() => {
    return () => {
      for (const p of padsRef.current) {
        if (p.status === 'ready') URL.revokeObjectURL(p.objectUrl)
      }
      disposeWorker()
    }
  }, [])

  const handleFile = useCallback(async (index: number, file: File) => {
    if (!isAudioFile(file)) {
      dispatch({
        type: 'error',
        index,
        sourceName: file.name,
        message: 'Not an audio file',
      })
      return
    }

    const previous = padsRef.current[index]
    if (previous.status === 'ready') {
      URL.revokeObjectURL(previous.objectUrl)
      if (playingIndexRef.current === index) {
        audioRef.current?.pause()
        setPlayingIndex(null)
      }
    }

    dispatch({ type: 'start', index, sourceName: file.name })
    try {
      const sniffed = await sniffSourceFormat(file)
      const targetKbps = sniffed
        ? computeTargetKbpsForSize(file.size, sniffed.kbps, TARGET_ENCODED_SIZE_BYTES)
        : undefined
      const { blob, skippedEncode } = await encodeToMp3(file, {
        onProgress: (percent) => {
          dispatch({ type: 'progress', index, percent })
        },
        targetKbps,
      })
      const objectUrl = URL.createObjectURL(blob)
      dispatch({
        type: 'done',
        index,
        blob,
        objectUrl,
        skippedEncode,
        sourceName: file.name,
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Encoding failed'
      dispatch({ type: 'error', index, sourceName: file.name, message })
    }
  }, [])

  const handlePlayToggle = useCallback((index: number) => {
    const pad = padsRef.current[index]
    if (pad.status !== 'ready') return
    const audio = audioRef.current
    if (!audio) return

    if (playingIndexRef.current === index) {
      audio.pause()
      setPlayingIndex(null)
      return
    }

    audio.src = pad.objectUrl
    audio.currentTime = 0
    audio.play().then(
      () => setPlayingIndex(index),
      () => setPlayingIndex(null),
    )
  }, [])

  const handleClear = useCallback((index: number) => {
    const pad = padsRef.current[index]
    if (pad.status === 'ready') {
      URL.revokeObjectURL(pad.objectUrl)
      if (playingIndexRef.current === index) {
        audioRef.current?.pause()
        setPlayingIndex(null)
      }
    }
    dispatch({ type: 'clear', index })
  }, [])

  const handleAudioEnded = () => setPlayingIndex(null)

  const intakeFiles = async (incoming: File[]) => {
    const files: File[] = []
    for (const f of incoming) {
      if (isZipFile(f)) {
        try {
          const inner = await extractZipAudio(f)
          inner.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
          files.push(...inner)
        } catch (err) {
          console.error('Failed to extract zip', f.name, err)
        }
      } else if (isAudioFile(f)) {
        files.push(f)
      }
    }
    if (files.length === 0) return

    const emptyIndices: number[] = []
    padsRef.current.forEach((p, i) => {
      if (p.status === 'empty') emptyIndices.push(i)
    })

    const count = Math.min(files.length, emptyIndices.length)
    const pairs = Array.from({ length: count }, (_, i) => ({
      index: emptyIndices[i],
      file: files[i],
    }))
    // Claim every target pad immediately so the UI shows the whole batch
    // as busy and a second drop can't grab the same slots mid-run.
    for (const { index, file } of pairs) {
      dispatch({ type: 'start', index, sourceName: file.name })
    }
    // Bounded pool: each encode starts with a main-thread decode holding
    // a live AudioContext (browsers cap those) and a full PCM buffer, so
    // an unbounded fan-out over a big drop can fail outright.
    let cursor = 0
    await Promise.all(
      Array.from({ length: Math.min(INTAKE_CONCURRENCY, pairs.length) }, async () => {
        while (cursor < pairs.length) {
          const { index, file } = pairs[cursor++]
          await handleFile(index, file)
        }
      }),
    )
  }

  const readyCount = pads.filter((p) => p.status === 'ready').length
  const hasReady = readyCount > 0
  const isEncoding = pads.some((p) => p.status === 'encoding')

  const toyMaker = useToyMaker({
    makerApp: 'music-player',
    hasPayload: hasReady,
    extraDisabled: isEncoding,
    firmwareTarget: MP3_PLAYER_FIRMWARE,
    appNameFallback: 'Oroblanco MP3 Player',
    buildPayload: async () => {
      const emptyBlob = await getEmptyMp3Blob()
      const payloadFiles: UploadFile[] = pads.map((p, i) => ({
        name: `${padLabel(i)}.mp3`,
        blob: p.status === 'ready' ? p.encodedBlob : emptyBlob,
      }))
      return { payloadFiles }
    },
  })

  return (
    <section className="music-player">
      <Link to="/" className="detail-back">&larr; Back to Toys</Link>
      <h1 className="page-title">Music Player</h1>
      <p className="page-lede">
        Drag an audio file onto each pad, or click an empty pad to choose a file. We&rsquo;ll convert
        each to the format the toy expects (mono MP3, 44.1&nbsp;kHz, 128&nbsp;kbps). Click a finished pad to
        preview the converted audio. There are {PAD_COUNT} slots.
      </p>

      <Dropzone
        onFiles={intakeFiles}
        accept="audio/*,.mp3,.wav,.aac,.m4a,.zip,application/zip"
      >
        <p className="music-player__dropzone-text">
          Drop <b>.mp3</b>s or a <b>.zip</b> file of <b>mp3</b>s here for automatic setup.<br/><br/>
          Did you know you can buy <b>.mp3</b>s on Amazon music?<br/>
          Just download the <b>.zip</b> from them and drop it here.
        </p>
      </Dropzone>

      <p className="music-player__dropzone-hint">Or be specific and drop <b>.mp3</b>s onto specific buttons</p>

      <div className="card music-player__panel">
        <PadGrid
          pads={pads}
          playingIndex={playingIndex}
          onFile={handleFile}
          onPlayToggle={handlePlayToggle}
          onClear={handleClear}
        />
      </div>

      <div className="music-player__actions">
        <span className="music-player__count">{readyCount} of {PAD_COUNT} ready</span>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!toyMaker.canSubmit}
          onClick={toyMaker.onDoneClick}
        >
          {toyMaker.isWorking ? 'Working…' : 'Done, make my toy'}
        </button>
      </div>

      <audio ref={audioRef} onEnded={handleAudioEnded} />

      {toyMaker.modals}
    </section>
  )
}
